import React, { memo, useCallback, useMemo, useRef, useState } from "react";
import { Form, Formik } from "formik";
import { Snackbar } from "@material-ui/core";

import SimpleModal from "../../components/SimpleModal";
import {
  WILL_APPLY_TO_ALL_ROUTES,
  DISASTER_RECOVERY,
} from "../../constants/strings";
import { validateFormValues } from "./validators";
import DisasterRecoveryForm from "./components/DisasterRecoveryForm";
import ModalControlled from "../../components/ModalControlled";
import { WarningIcon } from "../../components/icons";
import ConfirmSnackbar from "../../components/modals/ConfirmSnackbar";
import { wrapPromise } from "../../utils/promise";
import { getErrorMessage } from "../../utils/errors";
import { setCutOffTime } from "../../services/routes";
import { toMinutes } from "../../utils/date";
import useLocationQueryParams from "../../hooks/useLocationQueryParams";
import useLoader from "../../hooks/useLoader";
import { RECOVERY_ACCESS_PROCESS } from "../../enums/loaderProcess";

const RecoveryAccess = ({ open, onClose }) => {
  const form = useRef();
  const saveChangesModalRef = useRef();
  const [message, setErrorMessage] = useState("");
  const { date, depot } = useLocationQueryParams();
  const [showLoader, hideLoader] = useLoader();

  const initialValues = useMemo(() => {
    return {
      waveCutOff: "00:00",
      departTime: "00:00",
    };
  }, []);

  const closeSnackbar = useCallback(() => {
    setErrorMessage("");
  }, [setErrorMessage]);

  const handleConfirm = useCallback(async () => {
    const { waveCutOff, departTime } = form?.current?.values;
    try {
      const [, error] = await wrapPromise(
        saveChangesModalRef.current?.showModal()
      );
      if (error) return;
      showLoader(RECOVERY_ACCESS_PROCESS);
      const success = await setCutOffTime(depot, date, {
        waveCutOff: toMinutes(waveCutOff),
        departTime: toMinutes(departTime),
      });
      success && onClose();
    } catch (e) {
      setErrorMessage(getErrorMessage(e));
    } finally {
      hideLoader(RECOVERY_ACCESS_PROCESS);
    }
  }, [depot, date, showLoader, hideLoader, onClose]);

  return (
    <SimpleModal
      onClose={onClose}
      open={open}
      title={DISASTER_RECOVERY.toUpperCase()}
    >
      <Formik
        enableReinitialize
        innerRef={form}
        onSubmit={handleConfirm}
        initialValues={initialValues}
        validate={validateFormValues}
        validateOnChange={true}
      >
        {({ values, handleSubmit, dirty, isValid, initialValues }) => (
          <Form style={{ width: "100%" }}>
            <DisasterRecoveryForm
              values={values}
              handleConfirm={handleSubmit}
              dirty={dirty}
              isValid={isValid}
              initialValues={initialValues}
            />
          </Form>
        )}
      </Formik>
      <ModalControlled
        Component={ConfirmSnackbar}
        ref={saveChangesModalRef}
        messageIcon={<WarningIcon />}
      >
        {WILL_APPLY_TO_ALL_ROUTES}
      </ModalControlled>
      <Snackbar
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        open={!!message}
        autoHideDuration={2000}
        message={message}
        onClose={closeSnackbar}
      />
    </SimpleModal>
  );
};

export default memo(RecoveryAccess);
